import React from 'react';
import { isValidString } from '../../common/CommonUtilsMethods';
import { ToolTipCommonComponent } from '../../common/CommonComponents';
import {
  WgcFormFieldLabelProps,
  WgcFormFieldRequirement,
} from './WgcFormFieldLabelInterfaces';
import '../agency-pages/agencybilling2.css';

const DEFAULT_FIELD_HELPER_TEXT: string = 'A default is already set. You can leave this as-is.';
const CALCULATED_FIELD_HELPER_TEXT: string = 'This value is calculated automatically';

const getEmptyWhenZeroNumberInputValue = (
  numericValue: number | undefined | null
): string | number => {
  if (numericValue === undefined || numericValue === null || numericValue === 0) {
    return '';
  }
  return numericValue;
};

const parseNumberInputValue = (rawValue: string): number => {
  if (!isValidString(rawValue)) {
    return 0;
  }
  const parsedValue: number = Number(rawValue);
  if (!Number.isFinite(parsedValue)) {
    return 0;
  }
  return parsedValue;
};

const getDefaultHelperText = (defaultValueLabel: string | undefined): string => {
  if (!isValidString(defaultValueLabel)) {
    return DEFAULT_FIELD_HELPER_TEXT;
  }
  return `Default: ${defaultValueLabel}. You can leave this as-is.`;
};

const getRequirementHint = (requirement: WgcFormFieldRequirement): string | null => {
  if (requirement === WgcFormFieldRequirement.REQUIRED) {
    return '*';
  }
  if (requirement === WgcFormFieldRequirement.OPTIONAL) {
    return '(Optional)';
  }
  if (requirement === WgcFormFieldRequirement.DEFAULT) {
    return '(Default)';
  }
  return null;
};

const getHelperText = (
  requirement: WgcFormFieldRequirement,
  defaultValueLabel: string | undefined,
  helperText: string | undefined
): string | null => {
  if (isValidString(helperText)) {
    return helperText as string;
  }
  if (requirement === WgcFormFieldRequirement.DEFAULT) {
    return getDefaultHelperText(defaultValueLabel);
  }
  if (requirement === WgcFormFieldRequirement.CALCULATED) {
    return CALCULATED_FIELD_HELPER_TEXT;
  }
  return null;
};

const WgcFormFieldLabel = ({
  label,
  requirement,
  tooltipText,
  defaultValueLabel,
  helperText,
  htmlFor,
}: WgcFormFieldLabelProps): React.ReactElement => {
  const requirementHint: string | null = getRequirementHint(requirement);
  const resolvedHelperText: string | null = getHelperText(requirement, defaultValueLabel, helperText);
  const isRequiredAsterisk: boolean = requirement === WgcFormFieldRequirement.REQUIRED;

  const labelNode: React.ReactElement = (
    <label className="wgc-label" htmlFor={htmlFor}>
      {label}
      {requirementHint !== null && (
        <span className={isRequiredAsterisk ? 'wgc-required-asterisk' : 'wgc-field-requirement-hint'}>
          {isRequiredAsterisk ? ' *' : ` ${requirementHint}`}
        </span>
      )}
    </label>
  );

  return (
    <div className="wgc-form-field-label-block">
      {isValidString(tooltipText) ? (
        <ToolTipCommonComponent toolTipText={tooltipText as string}>
          {labelNode}
        </ToolTipCommonComponent>
      ) : (
        labelNode
      )}
      {resolvedHelperText !== null && <div className="wgc-helper-text">{resolvedHelperText}</div>}
    </div>
  );
};

export {
  WgcFormFieldLabel,
  getEmptyWhenZeroNumberInputValue,
  parseNumberInputValue,
};
